import { useEffect, useState } from 'react';

import IconButton from '@mui/material/IconButton';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDownToLine, Loader } from 'lucide-react';

import { createTanstackTable } from '../../common/utils';
import {
  useDownloadActualDataFile,
  useDownloadAllActualDataFiles,
  useGetActualData,
  useGetActualDataById,
  useGetActualDataSchemaFiles,
} from '../../hooks/tanstack/useActualDataHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import { type IfsStatus } from '../../types';
import cn from '../../utils/cn';
import TanstackTable from '../TanstackTable';

type IFSTableData = {
  id: number;
  name: string | null;
  status: IfsStatus;
  importedBy?: string | null;
  importDate?: string | null;
  downloadLink?: string | null;
};

const columnHelper = createColumnHelper<IFSTableData>();

const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    cell: (info) => {
      const id = info.row.original.id;

      const { orgId, projectId } = useGetRouteParams();

      const status = info.row.original.status;

      const { mutate: downloadActualDataFile, isPending: isDownloading } =
        useDownloadActualDataFile();

      const { data: actualDataDownloadFile } = useGetActualDataById({
        orgId,
        projectId,
        fileId: id,
      });

      return (
        <div className="text-start gap-1 flex flex-wrap break-all">
          {status === 'imported' && actualDataDownloadFile && (
            <button
              onClick={() =>
                downloadActualDataFile({
                  orgId,
                  projectId,
                  fileId: id,
                })
              }
              disabled={isDownloading}
              className={cn(
                'text-primary-bright-blue underline cursor-pointer',
                {
                  'pointer-events-none text-[#B3B3B3]': isDownloading,
                }
              )}
            >
              {info.getValue()}
            </button>
          )}
          {isDownloading && (
            <Loader
              size={22}
              className="animate-spin text-primary-bright-blue"
            />
          )}
          {status === 'not-imported' && <p>{info.getValue()}</p>}
        </div>
      );
    },
    header: () => <div className="flex text-nowrap text-start">Input File</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();

      const StatusLabel = ({
        label,
        value,
      }: {
        value: 'imported' | 'fail' | 'not-imported';
        label: string;
      }) => (
        <div className="flex justify-start">
          <p
            className={cn(
              'w-fit rounded-full capitalize bg-[#E1FBE7] px-6 py-1 text-green-600',
              {
                'bg-[#E1FBE7] text-green-600': value === 'imported',
                'text-red-600 bg-[#FFF5F2]': value === 'fail',
                'text-[#666] bg-gray-100': value === 'not-imported',
              }
            )}
          >
            {label}
          </p>
        </div>
      );

      switch (status) {
        case 'imported':
          return <StatusLabel value="imported" label="Imported" />;
        case 'fail':
          return <StatusLabel value="fail" label="Fail" />;
        case 'not-imported':
          return <StatusLabel value="not-imported" label="Not Imported" />;
      }
    },
    header: () => <div className="text-start">Status</div>,
  }),
  columnHelper.accessor('importedBy', {
    id: 'importedBy',
    cell: (info) => (
      <p className="py-1 text-[#333] capitalize">{info.getValue() ?? '--'}</p>
    ),
    header: () => (
      <div className="flex w-full shrink text-start">Imported By</div>
    ),
  }),
  columnHelper.accessor('importDate', {
    id: 'importDate',
    cell: (info) => (
      <p className="py-1 text-[#333]">
        {info.getValue()
          ? format(info.getValue() as string, 'y/MM/dd hh:mm a')
          : '--'}
      </p>
    ),
    sortingFn: (a, b) =>
      new Date(a.original.importDate as string).getTime() -
      new Date(b.original.importDate as string).getTime(),
    header: () => (
      <div className="flex w-full shrink text-start">Import Date</div>
    ),
  }),
  columnHelper.accessor('downloadLink', {
    id: 'downloadLink',
    cell: (info) => {
      const id = info.row.original.id;

      const { orgId, projectId } = useGetRouteParams();

      const { mutate: downloadActualDataFile, isPending: isDownloading } =
        useDownloadActualDataFile();

      const { data: actualDataDownloadFile } = useGetActualDataById({
        orgId,
        projectId,
        fileId: id,
      });

      return (
        <IconButton
          disabled={!actualDataDownloadFile?.file}
          onClick={() =>
            downloadActualDataFile({
              orgId,
              projectId,
              fileId: id,
            })
          }
        >
          {isDownloading ? (
            <Loader size={22} className="animate-spin" color="#2196f3" />
          ) : (
            <ArrowDownToLine
              color={actualDataDownloadFile?.file ? '#2196f3' : '#B3B3B3'}
              size={22}
            />
          )}
        </IconButton>
      );
    },
    enableSorting: false,
    header: (info) => {
      const { orgId, projectId } = useGetRouteParams();

      const { mutate: downloadAllActualDataFiles, isPending: isDownloading } =
        useDownloadAllActualDataFiles();

      const { data: actualDataAllDownloadFile } = useGetActualData({
        orgId,
        projectId,
      });

      const isDisabled = !actualDataAllDownloadFile?.length;

      return (
        <IconButton
          disabled={isDisabled || isDownloading}
          onClick={() =>
            downloadAllActualDataFiles({
              orgId,
              projectId,
            })
          }
          style={{
            color: isDisabled || isDownloading ? '#B3B3B3' : '#2196f3',
          }}
        >
          {isDownloading ? (
            <Loader size={22} className="animate-spin" />
          ) : (
            <ArrowDownToLine
              color={!isDisabled ? '#2196f3' : '#B3B3B3'}
              size={22}
            />
          )}
        </IconButton>
      );
    },
  }),
];

export const ActualDataTable = () => {
  const [data, setData] = useState<IFSTableData[]>([]);

  const { projectId, orgId } = useGetRouteParams();

  const table = createTanstackTable<IFSTableData>({
    data,
    columns,
  });

  const { data: actualData, isLoading: isLoadingActualData } = useGetActualData(
    {
      orgId,
      projectId,
    }
  );

  const { data: actualDataSchemas, isLoading: isLoadingActualDataSchemas } =
    useGetActualDataSchemaFiles({
      orgId,
      projectId,
    });

  useEffect(() => {
    if (!actualDataSchemas || !actualData) {
      return;
    }

    setData(
      actualDataSchemas.map((file) => ({
        id: file.file,
        name: file.name,
        status: file.is_imported ? 'imported' : 'not-imported',
        importDate: file.imported_at,
        importedBy:
          actualData?.find((item) => item.id === file.file)?.created_by
            ?.full_name ?? '--',
        downloadLink: '',
      }))
    );
  }, [actualDataSchemas, actualData]);

  return (
    <TanstackTable
      table={table}
      isLoading={isLoadingActualData || isLoadingActualDataSchemas}
      emptyState="No Actual data available.Please generate and upload your files."
    />
  );
};
